import { AlertTriangle, ArrowRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { humanizeMergeField, stepForMergeField } from '@/lib/agreements/constants';
import { labelForArea } from '@/components/contracts/setup/steps';

interface MergePreflightProps {
    /** `agreement_render`'s `missing[]` — every required field the data could not answer. */
    missing: string[];
    /**
     * Navigates to a setup step. The checklist is only useful if it can be acted on.
     *
     * THE FIELD TRAVELS WITH THE STEP, because on one dossier the step is not enough to route by.
     * `MERGE_FIELD_STEPS` sends the whole `insurance.` and `insurance_loan.` namespaces to the
     * single step `insurance` — which was exactly right while insurance was one area of contract
     * setup, and is three areas of the policy dossier: the period is on Policy, the payment to the
     * insurer is on Premium, and `insurance_loan.weekly_instalment` is on Advance. A host that can
     * tell them apart reads the second argument; every host that cannot ignores it and keeps its
     * one-argument handler.
     */
    onDeepLink: (step: string, field?: string) => void;
}

/**
 * The pre-flight checklist.
 *
 * A document whose merge data is incomplete still renders — SQL prints a fill-in rule
 * where the value belongs — which is exactly why it must not be sent: the driver would
 * sign a blank. Each row therefore names the field and links to the step that fills it.
 */
export default function MergePreflight({ missing, onDeepLink }: MergePreflightProps) {
    if (missing.length === 0) {
        return (
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400">
                <Check className="h-4 w-4 shrink-0" />
                Every merge field resolves.
            </div>
        );
    }

    return (
        <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
            <p className="flex items-center gap-2 text-sm font-medium text-destructive">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Cannot be sent — {missing.length} merge field{missing.length === 1 ? ' is' : 's are'} still empty
            </p>
            <ul className="mt-2.5 space-y-2">
                {missing.map((field) => {
                    const step = stepForMergeField(field);
                    return (
                        <li key={field} className="text-sm">
                            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                                {`{{${field}}}`}
                            </span>{' '}
                            <span className="text-muted-foreground">{humanizeMergeField(field)}</span>
                            {step && (
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="h-auto px-1.5 py-0 text-xs"
                                    onClick={() => onDeepLink(step, field)}
                                >
                                    <ArrowRight className="mr-1 h-3 w-3" />
                                    Resolve in {labelForArea(step)}
                                </Button>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
